import Vue from 'vue'
import TModal from './modal.vue'
import TButton from '../button/button.vue'
import Locale from '../../mixins/locale'
import { PREFIX_CLASS, PREFIX_ICON_CLASS } from '../../utils/constants'

const PREFIX_CONFIRM_CLASS = `${PREFIX_CLASS}modal-confirm`

function buildConfirmInstance() {
  let html = `<t-modal ref="modal"
             :visibled.sync="currentVisibled"
             :closable="false"
             :ok-text="okText"
             :cancel-text="cancelText"
             :header-visibled="false"
             :ok="ok"
             :cancel="cancel"
             custom-classes="${PREFIX_CONFIRM_CLASS}"
             width="456px"
    >
      <template v-slot>
        <div class="${PREFIX_CONFIRM_CLASS}-header">
          <div class="${PREFIX_CONFIRM_CLASS}-header-icon">
            <i :class="iconClasses"></i>
          </div>
          <div class="${PREFIX_CONFIRM_CLASS}-header-title">
            {{ title }}
          </div>
        </div>
        <div class="${PREFIX_CONFIRM_CLASS}-body">
          <div v-html="content"></div>
        </div>
      </template>
      <template v-slot:footer>
          <t-button v-if="currentCancelVisibled"
                    color="secondary"
                    @click.stop="$_onCloseClick">{{ localeCancelText }}</t-button>
          <t-button :color="okButtonColor"
                    :ghost="okButtonGhost"
                    :loading="currentLoading"
                    @click.stop="$_onOkClick">{{ localeOkText }}</t-button>
      </template>
    </t-modal>`
  let wrap = document.createElement('div')
  wrap.innerHTML = html
  let instance = new Vue({
    el: wrap,
    components: { TModal, TButton },
    mixins: [ Locale ],
    props: {
      level: {
        type: String,
        default: 'info',
        validator(val) {
          return ['info', 'success', 'warning', 'error', 'confirm'].includes(val)
        }
      },
      visibled: {
        type: Boolean,
        default: false
      },
      cancelVisibled: {
        type: Boolean,
        default: true
      },
      okButtonColor: {
        type: String,
        validator (value) {
          /* istanbul ignore next */
          return ['primary', 'secondary', 'success', 'info', 'warning', 'danger'].includes(value)
        },
        default: 'primary'
      },
      okButtonGhost: {
        type: Boolean,
        default: false
      },
      okText: {
        type: String,
        default: ''
      },
      cancelText: {
        type: String,
        default: ''
      },
      title: {
        type: String,
        default: ''
      },
      content: {
        type: String,
        default: ''
      },
      ok: {
        type: Function,
        default: () => {}
      },
      cancel: {
        type: Function,
        default: () => {}
      }
    },
    data() {
      return {
        currentVisibled: false,
        currentLoading: false,
        currentCancelVisibled: true
      }
    },
    computed: {
      iconClasses() {
        const iconPrefix = PREFIX_ICON_CLASS.replace('-', '')
        let prefix = `${iconPrefix} ${PREFIX_ICON_CLASS}`
        switch (this.level) {
          case 'success':
            return `${prefix}check-circle-outline text-success`
          case 'warning':
            return `${prefix}alert-circle-outline text-warning`
          case 'error':
            return `${prefix}close-circle-outline text-danger`
          case 'confirm':
            return `${prefix}help-circle-outline text-warning`
          default:
            return `${prefix}alert-circle-outline text-info`
        }
      },
      localeCancelText() {
        if (this.cancelText === '') {
          return this.t('t.modal.cancelText')
        } else {
          return this.cancelText
        }
      },
      localeOkText() {
        if (this.okText === '') {
          return this.t('t.modal.okText')
        } else {
          return this.okText
        }
      }
    },
    watch: {
      visibled(val) {
        this.currentVisibled = val
      },
      cancelVisibled(val) {
        this.currentCancelVisibled = val
      }
    },
    created() {
      this.currentVisibled = this.visibled
      this.currentCancelVisibled = this.cancelVisibled
    },
    methods: {
      $_isPromise(obj) {
        return typeof obj === 'object' && (typeof obj.then === 'function')
      },
      $_onCloseClick() {
        if (this.currentVisibled === true) {
          this.currentVisibled = false
          this.$emit('update:visibled', false)
          // this.$emit('cancel')
          this.cancel()
        }
      },
      $_onOkClick() {
        if (this.currentVisibled === true && this.currentLoading === false) {
          if (this.ok) {
            let result = this.ok()
            if (this.$_isPromise(result)) {
              this.currentLoading = true
              result.then((val) => {
                if (val !== false) {
                  this.currentVisibled = false
                  this.$emit('update:visibled', false)
                }
                this.currentLoading = false
              })
            } else if (result !== false) {
              this.currentVisibled = false
              this.$emit('update:visibled', false)
            }
          } else {
            this.currentVisibled = false
            this.$emit('update:visibled', false)
          }
        }
      },
      show() {
        this.currentVisibled = true
        this.$emit('update:visibled', false)
      },
      destroy() {
        this.$destroy()
      }
    }
  })
  return instance
}

/**
 * @class
 * 确认对话框管理单例类
 */
class Confirm {
  constructor () {
    this._defProps = {
      okButtonColor: 'primary',
      okButtonGhost: false,
      title: '',
      content: ''
    }
    this._info = null
    this._success = null
    this._warning = null
    this._error = null
    this._confirm = null
  }
  _setInstance(instance, props) {
    for (let p in props) {
      instance[p] = props[p]
    }
  }
  info(props = {}) {
    props = Object.assign({}, this._defProps, props)
    props.level = 'info'
    props.cancelVisibled = false
    if (this._info === null) {
      this._info = buildConfirmInstance()
    }
    this._setInstance(this._info, props)
    this._info.show()
  }
  success(props = {}) {
    props = Object.assign({}, this._defProps, props)
    props.level = 'success'
    props.cancelVisibled = false
    if (this._success === null) {
      this._success = buildConfirmInstance()
    }
    this._setInstance(this._success, props)
    this._success.show()
  }
  warning(props = {}) {
    props.level = 'warning'
    props.cancelVisibled = false
    props = Object.assign({}, this._defProps, props)
    if (this._warning === null) {
      this._warning = buildConfirmInstance()
    }
    this._setInstance(this._warning, props)
    this._warning.show()
  }
  error(props = {}) {
    props.level = 'error'
    props.cancelVisibled = false
    props.okButtonGhost = props.okButtonGhost || true
    props.okButtonColor = props.okButtonColor || 'danger'
    props = Object.assign({}, this._defProps, props)
    if (this._error === null) {
      this._error = buildConfirmInstance()
    }
    this._setInstance(this._error, props)
    this._error.show()
  }
  confirm(props = {}) {
    props = Object.assign({}, this._defProps, props)
    props.level = 'confirm'
    props.cancelVisibled = true
    if (this._confirm === null) {
      this._confirm = buildConfirmInstance()
    }
    this._setInstance(this._confirm, props)
    this._confirm.show()
  }
  /**
   * 隐藏所有对话框实例
   */
  clear() {
    if (this._info !== null) {
      this._info.visibled = false
    }
    if (this._success !== null) {
      this._success.visibled = false
    }
    if (this._warning !== null) {
      this._warning.visibled = false
    }
    if (this._error !== null) {
      this._error.visibled = false
    }
    if (this._confirm !== null) {
      this._confirm.visibled = false
    }
  }
  /**
   * 销毁指定类型对话框实例
   */
  destroy(type) {
    let name = '_' + type
    if (this[name] !== null) {
      this[name].destroy()
      this[name] = null
    }
  }
}

const confirmInstance = new Confirm()

export default confirmInstance



// WEBPACK FOOTER //
// ./src/module/modal/confirm.js